/**
 * Sprint 17 T17.19 — Web admin structured logger.
 *
 * `console.log` yerine kullan. Production'da Sentry breadcrumb'a yazılır,
 * dev'de console'a JSON çıktı verir. Tüm log'lar `module` etiketi ile
 * gruplanır → "hangi feature'dan geliyor?" sorgusu kolay.
 *
 * Kullanım:
 *   const log = createLogger('payments');
 *   log.info('budget loaded', { tenantId, count: items.length });
 *   log.error('funding failed', err, { amount });
 *
 * PII güvenliği:
 *   - TCKN/VKN/IBAN/PAN/CVV/JWT alanları beforeSend hook ile redact
 *     (`sentry.ts` — Sprint 17 T17.18 ile genişler)
 *   - Bilinçli olarak PII logla: NEVER — `userId` (UUID) ok, `email` ok
 *     ama `tckn` veya `iban` ASLA
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

interface LogContext {
  [key: string]: unknown;
}

export interface Logger {
  debug(msg: string, ctx?: LogContext): void;
  info(msg: string, ctx?: LogContext): void;
  warn(msg: string, ctx?: LogContext): void;
  error(msg: string, err?: unknown, ctx?: LogContext): void;
  child(extraCtx: LogContext): Logger;
}

/** Vite env'den minimum level. Default: dev=debug, prod=info. */
function configuredLevel(): LogLevel {
  const fromEnv = (import.meta.env.VITE_LOG_LEVEL ?? '').toLowerCase() as LogLevel;
  if (fromEnv in LEVEL_PRIORITY) return fromEnv;
  return import.meta.env.PROD ? 'info' : 'debug';
}

const MIN_LEVEL = LEVEL_PRIORITY[configuredLevel()];

// Lookup `key.toLowerCase()` ile yapıldığı için set'teki tüm anahtarlar lowercase.
// Eskiden camelCase'di → idempotencyKey/providerRef/bankAccountIban/cardNumber/
// currentPassword/accessToken/refreshToken hiçbir zaman match etmiyordu (sessiz fail).
const PII_FIELDS = new Set([
  'tckn',
  'vkn',
  'iban',
  'pan',
  'cardnumber',
  'cvv',
  'password',
  'currentpassword',
  'accesstoken',
  'refreshtoken',
  'authorization',
  'idempotencykey',
  'providerref',
  'bankaccountiban',
]);

/** Recursive redact — PII alanlarını `[REDACTED]` ile değiştir. */
function redact(input: unknown, depth = 0): unknown {
  if (depth > 5) return '[MAX_DEPTH]';
  if (input === null || input === undefined) return input;
  if (Array.isArray(input)) return input.map((v) => redact(v, depth + 1));
  if (typeof input !== 'object') return input;

  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    if (PII_FIELDS.has(key.toLowerCase())) {
      out[key] = '[REDACTED]';
    } else {
      out[key] = redact(value, depth + 1);
    }
  }
  return out;
}

function emit(level: LogLevel, module: string, msg: string, baseCtx: LogContext, extra?: LogContext): void {
  if (LEVEL_PRIORITY[level] < MIN_LEVEL) return;

  const redactedCtx = redact({ ...baseCtx, ...extra }) as Record<string, unknown>;

  const payload = {
    time: new Date().toISOString(),
    level,
    module,
    msg,
    ...redactedCtx,
  } as Record<string, unknown>;

  // Sprint 17 T17.18 — Sentry breadcrumb trail (lazy import; sentry.ts no-op olabilir).
  // Direkt import yerine global lookup → circular dep + bundler weight'i artırmaz.
  const sentryHook = (
    globalThis as unknown as {
      __custelSentryBreadcrumb?: (lvl: LogLevel, mod: string, m: string, d?: Record<string, unknown>) => void;
    }
  ).__custelSentryBreadcrumb;
  if (sentryHook && level !== 'debug') {
    try {
      sentryHook(level, module, msg, redactedCtx);
    } catch {
      // breadcrumb'ı atlama — log çıktısı bozulmamalı
    }
  }

  if (level === 'error') {
    console.error(payload);
  } else if (level === 'warn') {
    console.warn(payload);
  } else {
    // eslint-disable-next-line no-console
    console.log(payload);
  }
}

/** Hata objesini serializable forma çevir (Sentry'den gelebilir, custom Error olabilir). */
function serializeError(err: unknown): Record<string, unknown> {
  if (err instanceof Error) {
    return {
      err: {
        name: err.name,
        message: err.message,
        stack: err.stack,
      },
    };
  }
  return { err };
}

export function createLogger(module: string, baseCtx: LogContext = {}): Logger {
  return {
    debug(msg, ctx) {
      emit('debug', module, msg, baseCtx, ctx);
    },
    info(msg, ctx) {
      emit('info', module, msg, baseCtx, ctx);
    },
    warn(msg, ctx) {
      emit('warn', module, msg, baseCtx, ctx);
    },
    error(msg, err, ctx) {
      emit('error', module, msg, baseCtx, { ...serializeError(err), ...ctx });
    },
    child(extraCtx) {
      return createLogger(module, { ...baseCtx, ...extraCtx });
    },
  };
}
